import { Target, Zap, Award } from "lucide-react";

import janAngelImg from "@/assets/team/jan-angel.png";
import marcZwolsmanImg from "@/assets/team/marc-zwolsman.png";
import { useLanguage } from "@/contexts/LanguageContext";
import { translations } from "@/i18n/translations";

const About = () => {
  const { language } = useLanguage();
  const t = translations[language].about;

  const teamPhotos = [janAngelImg, marcZwolsmanImg];

  return (
    <section id="about" className="py-14 sm:py-24 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="max-w-4xl mx-auto mb-10 sm:mb-16 md:mb-24">
          {/* Content */}
          <div className="animate-fade-in">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight mb-6 px-4 lg:px-0">
              {t.title} <span className="text-primary">PlayfieldVR</span>
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 px-4 lg:px-0">{t.description1}</p>
            <p className="text-base sm:text-lg text-muted-foreground mb-6 sm:mb-8 px-4 lg:px-0">{t.description2}</p>

            <div className="space-y-4 sm:space-y-6 px-4 lg:px-0">
              <div className="flex items-start gap-3 sm:gap-4">
                <Target className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">{t.mission.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{t.mission.description}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">{t.innovation.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{t.innovation.description}</p>
                </div>
              </div>

              <div className="flex items-start gap-3 sm:gap-4">
                <Award className="w-5 h-5 sm:w-6 sm:h-6 text-primary flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">{t.quality.title}</h3>
                  <p className="text-sm sm:text-base text-muted-foreground">{t.quality.description}</p>
                </div>
              </div>
            </div>
          </div>

        </div>

        {/* Team Section */}
        <div className="relative">
          <div className="text-center mb-8 sm:mb-12 px-4 lg:px-0">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">{t.team.title}</h2>
            <p className="text-base sm:text-lg text-muted-foreground">{t.team.subtitle}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 px-4 lg:px-0">
            {t.team.members.map((member, index) => (
              <article
                key={index}
                className="relative animate-fade-in group"
                style={{ animationDelay: `${0.2 + index * 0.1}s` }}
              >
                <div className="bg-transparent border border-border rounded-2xl p-8 hover:border-primary/50 transition-colors duration-300">
                  {/* Photo with subtle border */}
                  <div className="relative w-32 h-32 sm:w-40 sm:h-40 mx-auto mb-6">
                    <img
                      src={teamPhotos[index]}
                      alt={`${member.name} - ${member.role} bij PlayfieldVR`}
                      className="relative rounded-full w-full h-full object-cover"
                    />
                  </div>

                  {/* Name and Role */}
                  <div className="text-center mb-4">
                    <h3 className="text-xl sm:text-2xl font-bold mb-1">{member.name}</h3>
                    <p className="text-sm text-muted-foreground font-medium">{member.role}</p>
                  </div>

                  {/* Bio */}
                  <p className="text-base text-muted-foreground text-center leading-relaxed">{member.bio}</p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
